// The pause payload's `status` arrives as a backend token ("started",
// "not_started", ...) while the whole pause UI — the exercise card highlight,
// StatusChip's lookup table, "current exercise" detection and the completed
// counter — compares against these numbers. Everything funnels through
// normalizeExerciseStatus so a string status can't silently match nothing.
export const EXERCISE_STATUS = Object.freeze({
  NOT_STARTED: 1,
  CURRENT: 2,
  INCOMPLETE: 3,
  SKIPPED: 4,
  COMPLETED: 5,
});

const STATUS_TOKEN_TO_CODE = Object.freeze({
  notstarted: EXERCISE_STATUS.NOT_STARTED,
  pending: EXERCISE_STATUS.NOT_STARTED,
  waiting: EXERCISE_STATUS.NOT_STARTED,
  upcoming: EXERCISE_STATUS.NOT_STARTED,
  idle: EXERCISE_STATUS.NOT_STARTED,

  started: EXERCISE_STATUS.CURRENT,
  inprogress: EXERCISE_STATUS.CURRENT,
  running: EXERCISE_STATUS.CURRENT,
  active: EXERCISE_STATUS.CURRENT,
  current: EXERCISE_STATUS.CURRENT,
  performing: EXERCISE_STATUS.CURRENT,

  incomplete: EXERCISE_STATUS.INCOMPLETE,
  unfinished: EXERCISE_STATUS.INCOMPLETE,
  partial: EXERCISE_STATUS.INCOMPLETE,
  failed: EXERCISE_STATUS.INCOMPLETE,

  skipped: EXERCISE_STATUS.SKIPPED,
  skip: EXERCISE_STATUS.SKIPPED,

  completed: EXERCISE_STATUS.COMPLETED,
  complete: EXERCISE_STATUS.COMPLETED,
  finished: EXERCISE_STATUS.COMPLETED,
  done: EXERCISE_STATUS.COMPLETED,
});

function normalizeToken(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

/**
 * Maps a backend exercise status onto the numeric EXERCISE_STATUS code the UI
 * uses. Numbers pass through unchanged; an unknown token returns null so the
 * caller renders the same "no chip" state it already did, instead of guessing.
 */
export function normalizeExerciseStatus(status) {
  if (typeof status === 'number') {
    return Number.isFinite(status) ? status : null;
  }

  if (typeof status !== 'string' || !status.trim()) {
    return null;
  }

  const token = normalizeToken(status);

  // Numeric strings ("2") come from the same enum, just stringified.
  if (/^\d+$/.test(token)) {
    return Number(token);
  }

  return STATUS_TOKEN_TO_CODE[token] ?? null;
}

// Missing/non-numeric order values sort to the end rather than throwing or
// landing arbitrarily first — an item the backend didn't rank shouldn't jump
// the queue.
function toOrderValue(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : Infinity;
}

// `order` is the only per-item sequencing field pauseStage entries carry
// (1, 2, 3, ...). `exercise_order` is NOT one of them — it rides on the
// message itself (see resolvePauseStageContainer's caller in
// use-websocket-connection.jsx), naming which exercise's `order` is currently
// active. Array.prototype.sort is stable, so an item missing `order` keeps
// its backend-given relative position instead of jumping the queue.
function comparePauseStageOrder(a, b) {
  return toOrderValue(a?.order) - toOrderValue(b?.order);
}

/**
 * Returns the pause payload with every item's `status` normalized to a numeric
 * code, keeping the original token under `statusToken` for debugging, and
 * sorted by `order` — the backend doesn't guarantee pauseStage arrives in
 * display order.
 */
export function normalizePauseStageItems(items) {
  if (!Array.isArray(items)) {
    return items;
  }

  return items
    .map(item => ({
      ...item,
      statusToken: item?.status,
      status: normalizeExerciseStatus(item?.status),
    }))
    .sort(comparePauseStageOrder);
}
